﻿// ============================================================================
// MÃ“DULO 9: FINANCIAL MANAGER ERP (V11.6 - DYNAMIC ANALYTICS)
// ARQUIVO: src/9_Financial_Manager.jsx
// OBJETIVO: KPIs Reativos Ã  Busca (BI em Tempo Real) e CSV Contextual.
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import { FIREBASE_CONFIG } from '../../config/Config';
import {
  loadOfficeFinanceSettings,
  saveOfficeCategories,
  saveOfficeInitialBalance,
  loadOfficeTemplates,
  loadOfficePostingsByMonth,
  loadOfficePostingsByYear,
  findOfficePostingsByClient,
  updateOfficePosting,
  createOfficeTemplate,
  createOfficePosting,
  deleteOfficePosting,
  markOfficePostingPaid,
  fetchClientsForDropdown,
  fetchProcessesForDropdown,
} from '../../services/OfficeFinanceRepository.js';
import { logClientPortalEvent } from '../../utils/portalEventLog';
import { toTitleCasePt } from '../../utils/formatters';
import { SaveCancelBar } from '../../components/ui/SaveCancelBar';
import {
  FinanceSectionHeader,
  PendenciasUrgentesPanel,
  useJsonPendencias,
  postingsToPendencyItems,
} from './_shared/FinanceSharedUI';

// --- UTILITÃRIOS NATIVOS ---
const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
const formatCompact = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: "compact" }).format(val);

// --- 1. CONFIGURAÃ‡ÃƒO JURÃDICA ---
const DEFAULT_OFFICE_CATEGORIES = {
    RECEITA_BRUTA: { 
        label: "1. RECEITA BRUTA (Honorários)", 
        color: "text-green-800 bg-green-50 border-green-200", 
        subs: ["Honorários de Partido", "Honorários Contratuais", "Honorários de ÃŠxito", "Sucumbência", "Consultoria Avulsa"], 
        budget: 0 
    },
    CUSTOS_DIRETOS: { 
        label: "2. CUSTOS DIRETOS (CSP)", 
        color: "text-yellow-800 bg-yellow-50 border-yellow-200", 
        subs: ["Repasse a Parceiros (Split)", "Correspondentes", "Custas Absorvidas", "Comissões"], 
        budget: 0 
    },
    DESPESAS_OP: { 
        label: "3. DESPESAS OPERACIONAIS", 
        color: "text-red-700 bg-red-50 border-red-200", 
        subs: ["Pessoal/Sócios", "Ocupação/Aluguel", "Software & Tech", "Marketing Jurídico", "Impostos"], 
        budget: 5000 
    },
    CONTA_CLIENTE: { 
        label: "4. GESTÃƒO DE TERCEIROS (TRUST)", 
        color: "text-blue-800 bg-blue-50 border-blue-200", 
        description: "Valores de Clientes (Suprimentos/Alvarás)",
        subs: ["Entrada de Suprimentos", "Levantamento de Alvará", "Pagamento de Guia (Saída)", "Repasse ao Cliente"], 
        budget: 0 
    }
};

const OFFICE_WALLETS = [
    "Conta Operacional (Itaú)",
    "Conta Trust (Clientes)",
    "Caixinha (Pequenas Despesas)",
    "Cartão Corporativo"
];

// --- 2. UTILITÃRIO DE EXPORTAÃ‡ÃƒO CSV (ENRICHED) ---
const exportToCSV = (data, filename = 'financeiro_escritorio.csv') => {
    if (!data || !data.length) return alert("Sem dados para exportar.");
    
    const headers = ["Vencimento", "Competência", "Descrição", "Vínculo (Nome)", "Vínculo (Tipo)", "Grupo", "Categoria", "Conta", "Movimento", "Valor", "Status"];
    
    const rows = data.map(row => [
        row.date,
        row.competenceDate || row.date,
        `"${row.description}"`,
        `"${row.linkedName || ''}"`,
        row.linkedToType,
        row.group,
        row.category,
        row.account || "Geral",
        row.movementType === 'CREDIT' ? 'ENTRADA' : 'SAIDA',
        row.value,
        row.status
    ]);

    const csvContent = [
        headers.join(','),
        ...rows.map(e => e.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// --- 3. MODAIS E COMPONENTES AUXILIARES ---

// MODAL DRILL-DOWN
const DrillDownModal = ({ onClose, data, title, monthLabel }) => {
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border-t-4 border-blue-800">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <div><p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{monthLabel}</p><h3 className="text-lg font-bold text-gray-800">{title}</h3></div>
                    <button onClick={onClose}><i className="fas fa-times text-gray-400 hover:text-red-500 text-lg"></i></button>
                </div>
                <div className="p-0 max-h-96 overflow-y-auto">
                    {data.items.length === 0 ? <div className="p-8 text-center text-gray-400 text-sm">Nenhum lançamento.</div> : (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-xs text-gray-500 uppercase font-bold sticky top-0"><tr><th className="p-3 text-left">Descrição</th><th className="p-3 text-left">Ref.</th><th className="p-3 text-right">Valor</th></tr></thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.items.map((item, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="p-3"><div className="font-bold text-gray-700">{item.description}</div><div className="text-[10px] text-gray-400">{item.category}</div></td>
                                        <td className="p-3 text-xs text-gray-500 truncate max-w-[100px]">{item.linkedName ? <span className="bg-blue-100 text-blue-700 px-1 rounded">{toTitleCasePt(item.linkedName)}</span> : '-'}</td>
                                        <td className={`p-3 text-right font-mono font-bold ${item.movementType === 'CREDIT' ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(item.value)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50 font-bold border-t border-gray-200"><tr><td colSpan="2" className="p-3 text-right text-gray-600">TOTAL</td><td className="p-3 text-right text-brand-blue">{formatCurrency(data.total)}</td></tr></tfoot>
                        </table>
                    )}
                </div>
                <div className="p-3 bg-gray-50 text-center border-t border-gray-100"><button onClick={onClose} className="text-xs font-bold text-gray-500 hover:text-gray-800">FECHAR</button></div>
            </div>
        </div>
    );
};

// MODAL CATEGORIAS
const CategoryManagerModal = ({ onClose, categories, onSaveCategories }) => {
    const [localCats, setLocalCats] = useState(categories || DEFAULT_OFFICE_CATEGORIES);
    const [newSub, setNewSub] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('RECEITA_BRUTA');

    const handleAddSub = () => {
        if (!newSub.trim()) return;
        const updated = { ...localCats };
        if (!updated[selectedGroup].subs) updated[selectedGroup].subs = [];
        updated[selectedGroup].subs.push(newSub);
        setLocalCats({ ...updated });
        setNewSub('');
    };
    const handleDeleteSub = (group, sub) => {
        const updated = { ...localCats };
        if (updated[group]?.subs) { updated[group].subs = updated[group].subs.filter(s => s !== sub); setLocalCats({ ...updated }); }
    };
    const handleBudgetChange = (group, value) => {
         const updated = { ...localCats };
         updated[group].budget = Number(value);
         setLocalCats({ ...updated });
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border-t-4 border-blue-600">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50"><h3 className="text-lg font-bold text-gray-800">Plano de Contas (Escritório)</h3><button onClick={onClose} className="text-gray-400 hover:text-red-500"><i className="fas fa-times text-xl"></i></button></div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Grupo Contábil</label><select className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none" value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)}>{Object.entries(localCats).map(([key, val]) => <option key={key} value={key}>{val.label}</option>)}</select></div>
                        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Meta Mensal</label><input type="number" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={localCats[selectedGroup]?.budget || 0} onChange={e => handleBudgetChange(selectedGroup, e.target.value)} /></div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-64 overflow-y-auto custom-scrollbar">
                        <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Contas Analíticas</p>
                        <div className="space-y-2">{localCats[selectedGroup]?.subs?.map((sub, idx) => (<div key={idx} className="flex justify-between items-center bg-white p-3 rounded-md border border-gray-100 shadow-sm"><span className="text-sm text-gray-700 font-medium">{sub}</span><button onClick={() => handleDeleteSub(selectedGroup, sub)} className="text-gray-300 hover:text-red-500"><i className="fas fa-trash"></i></button></div>))}</div>
                    </div>
                    <div className="flex gap-2"><input data-scb-ignore-enter="true" className="flex-1 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Nova Conta..." value={newSub} onChange={e => setNewSub(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddSub()} /><button onClick={handleAddSub} className="bg-blue-600 text-white px-4 rounded-lg text-sm font-bold hover:bg-blue-700">ADD</button></div>
                </div>
                <div className="p-4 bg-gray-50 border-t border-gray-100">
                    <SaveCancelBar
                        onCancel={onClose}
                        onSave={() => {
                            onSaveCategories(localCats);
                            onClose();
                        }}
                        cancelLabel="Cancelar"
                        saveLabel="Salvar"
                    />
                </div>
            </div>
        </div>
    );
};

// MODAL SALDO INICIAL
const InitialBalanceModal = ({ onClose, onSave, currentValue }) => {
    const [balance, setBalance] = useState(currentValue || 0);
    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden border-t-4 border-blue-800">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <h3 className="text-lg font-bold text-gray-800">Saldo Inicial (Dia Zero)</h3>
                    <button onClick={onClose}><i className="fas fa-times text-gray-400 hover:text-red-500"></i></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="bg-blue-50 p-3 rounded text-xs text-blue-800 mb-2">
                        Insira o saldo consolidado atual (Operacional + Trust) para calibrar o gráfico de liquidez.
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Saldo Atual Total</label>
                        <input type="number" step="0.01" className="w-full p-3 border border-gray-300 rounded-lg text-lg font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none text-center" value={balance} onChange={e => setBalance(e.target.value)} />
                    </div>
                </div>
                <div className="p-4 bg-gray-50 border-t border-gray-100">
                    <SaveCancelBar
                        onCancel={onClose}
                        onSave={() => onSave(balance)}
                        cancelLabel="Cancelar"
                        saveLabel="Salvar"
                    />
                </div>
            </div>
        </div>
    );
};

// MODAL LANÃ‡AMENTO (COM EDIÃ‡ÃƒO, TRUST FIX E SANITIZAÃ‡ÃƒO)
const OfficeTransactionModal = ({ onClose, onSave, categories, history, initialData }) => {
    const [mode, setMode] = useState('SINGLE'); 
    const [form, setForm] = useState({
        description: '', amount: '', type: 'DESPESAS_OP', subCategory: '', account: '',
        date: new Date().toISOString().split('T')[0],
        competenceDate: new Date().toISOString().slice(0, 7),
        repeatMode: 'SINGLE', repeatCount: 1, isPaid: false,
        linkedToType: 'NONE', linkedId: '', linkedName: '',
        movementType: 'DEBIT' 
    });
    
    const [clients, setClients] = useState([]);
    const [processes, setProcesses] = useState([]);

    useEffect(() => {
        if (initialData) {
            setForm({
                ...initialData,
                movementType: initialData.movementType || (initialData.group === 'RECEITA_BRUTA' ? 'CREDIT' : 'DEBIT')
            });
            if (initialData.repeatMode) setMode(initialData.repeatMode);
        }
    }, [initialData]);

    useEffect(() => {
        if (!initialData) {
            if (form.type === 'RECEITA_BRUTA') setForm(prev => ({ ...prev, movementType: 'CREDIT' }));
            else if (form.type === 'CONTA_CLIENTE') { /* Trust: Usuário decide */ }
            else setForm(prev => ({ ...prev, movementType: 'DEBIT' }));
        }
    }, [form.type]);

    useEffect(() => {
        const loadLinks = async () => {
            try {
                const clientList = await fetchClientsForDropdown();
                setClients(clientList);
                const procList = await fetchProcessesForDropdown();
                setProcesses(procList);
            } catch (e) { console.error(e); }
        };
        loadLinks();
    }, []);

    const suggestions = useMemo(() => {
        if (!history) return [];
        return [...new Set(history.map(t => t.description.split(' (')[0]))].sort();
    }, [history]);

    const currentCategory = (categories && categories[form.type]) ? categories[form.type] : { subs: [] };
    const subs = currentCategory.subs || [];

    const handleLinkChange = (e) => {
        const id = e.target.value;
        const type = form.linkedToType;
        let name = '';
        if (type === 'CLIENT') name = clients.find(c => c.id === id)?.name;
        if (type === 'PROCESS') name = processes.find(p => p.id === id)?.label;
        setForm({ ...form, linkedId: id, linkedName: name || '' });
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/70 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border-t-4 border-blue-800 transform scale-100">
                <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/80">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <i className="fas fa-briefcase text-blue-800"></i> {initialData ? 'Editar Lançamento' : 'Lançamento Escritório'}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-red-500"><i className="fas fa-times text-lg"></i></button>
                </div>
                <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto custom-scrollbar">
                    
                    <div className="flex bg-gray-100 p-1 rounded-lg">
                        <button onClick={() => setForm({...form, movementType: 'CREDIT'})} className={`flex-1 py-2 rounded-md text-xs font-bold ${form.movementType === 'CREDIT' ? 'bg-green-100 text-green-700 shadow-sm' : 'text-gray-500'}`}>
                            <i className="fas fa-arrow-down mr-1"></i> ENTRADA (+)
                        </button>
                        <button onClick={() => setForm({...form, movementType: 'DEBIT'})} className={`flex-1 py-2 rounded-md text-xs font-bold ${form.movementType === 'DEBIT' ? 'bg-red-100 text-red-700 shadow-sm' : 'text-gray-500'}`}>
                            <i className="fas fa-arrow-up mr-1"></i> SAÃDA (-)
                        </button>
                    </div>

                    <div className="bg-blue-50 p-3 rounded border border-blue-100">
                         <label className="block text-[10px] font-bold text-blue-800 uppercase mb-1">Centro de Custo / Vínculo</label>
                         <select className="w-full p-2 border rounded text-sm mb-2 bg-white" value={form.linkedToType} onChange={e => setForm({...form, linkedToType: e.target.value, linkedId: '', linkedName: ''})}>
                             <option value="NONE">Despesa Geral / Administrativa</option>
                             <option value="CLIENT">Reembolsável (Cliente)</option>
                             <option value="PROCESS">Custas Processuais</option>
                         </select>
                         {form.linkedToType === 'CLIENT' && <select className="w-full p-2 border rounded text-sm bg-white" value={form.linkedId} onChange={handleLinkChange}><option value="">Selecione o Cliente...</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}
                         {form.linkedToType === 'PROCESS' && <select className="w-full p-2 border rounded text-sm bg-white" value={form.linkedId} onChange={handleLinkChange}><option value="">Selecione o Processo...</option>{processes.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}</select>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Grupo</label><select className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white" value={form.type} onChange={e => setForm({...form, type: e.target.value, subCategory: ''})}>{Object.entries(categories).map(([key, val]) => (<option key={key} value={key}>{val.label}</option>))}</select></div>
                        <div><label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Categoria</label><select className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white" value={form.subCategory} onChange={e => setForm({...form, subCategory: e.target.value})}><option value="">Selecione...</option>{subs.map(sub => (<option key={sub} value={sub}>{sub}</option>))}</select></div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                        <div className="col-span-2"><label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Descrição</label><input list="desc-list" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Ex: Honorários..." /><datalist id="desc-list">{suggestions.map((s,i) => <option key={i} value={s}/>)}</datalist></div>
                        <div><label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Conta</label><select className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white" value={form.account} onChange={e => setForm({...form, account: e.target.value})}><option value="">...</option>{OFFICE_WALLETS.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Valor (R$)</label><input type="number" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm font-bold text-gray-700" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} /></div>
                        <div><label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Vencimento (Caixa)</label><input type="date" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div>
                    </div>

                    {!initialData && (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2">Recorrência</label>
                            <div className="flex gap-2 mb-2">
                                <button onClick={() => setForm({...form, repeatMode: 'SINGLE', repeatCount: 1})} className={`flex-1 py-1 px-2 rounded text-xs font-bold border ${form.repeatMode === 'SINGLE' ? 'bg-white border-blue-500 text-blue-700' : 'border-transparent text-gray-500 hover:bg-gray-100'}`}>Ãšnico</button>
                                <button onClick={() => setForm({...form, repeatMode: 'INSTALLMENT', repeatCount: 12})} className={`flex-1 py-1 px-2 rounded text-xs font-bold border ${form.repeatMode === 'INSTALLMENT' ? 'bg-white border-purple-500 text-purple-700' : 'border-transparent text-gray-500 hover:bg-gray-100'}`}>Parcelado</button>
                                <button onClick={() => setForm({...form, repeatMode: 'RECURRING', repeatCount: 12})} className={`flex-1 py-1 px-2 rounded text-xs font-bold border ${form.repeatMode === 'RECURRING' ? 'bg-white border-orange-500 text-orange-700' : 'border-transparent text-gray-500 hover:bg-gray-100'}`}>Recorrente</button>
                            </div>
                            {form.repeatMode !== 'SINGLE' && <input type="number" className="w-16 p-1 border rounded text-center text-sm font-bold" value={form.repeatCount} onChange={e => setForm({...form, repeatCount: e.target.value})} />}
                        </div>

                        {mode === 'SINGLE' && (
                            <div className="border-t border-gray-200 pt-3">
                                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Mês de Competência (Fato Gerador)</label>
                                <div className="flex items-center gap-2"><input type="month" className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:border-blue-500" value={form.competenceDate} onChange={e => setForm({...form, competenceDate: e.target.value})} /></div>
                            </div>
                        )}
                    </div>
                    )}

                    {mode === 'SINGLE' && (
                        <label className="flex items-center gap-3 cursor-pointer group pt-2 border-t border-gray-200">
                            <input type="checkbox" className="peer sr-only" checked={form.isPaid} onChange={e => setForm({...form, isPaid: e.target.checked})} />
                            <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-green-500"></div>
                            <div className="absolute left-1 top-1 w-3 h-3 bg-white rounded-full peer-checked:translate-x-5"></div>
                            <span className={`text-sm font-bold ${form.isPaid ? 'text-green-600' : 'text-gray-400'}`}>{form.isPaid ? 'Pago / Liquidado' : 'Pendente / A Receber/Pagar'}</span>
                        </label>
                    )}
                </div>
                <div className="p-5 bg-gray-50 border-t border-gray-100">
                    <SaveCancelBar
                        onCancel={onClose}
                        onSave={() => onSave(form, form.repeatMode)}
                        cancelLabel="Cancelar"
                        saveLabel="Confirmar"
                    />
                </div>
            </div>
        </div>
    );
};
const DailyLiquidityPanel = ({ postings, initialBalance }) => {
    const dailyData = useMemo(() => {
        const map = {};
        const sorted = [...postings].sort((a,b) => new Date(a.date + 'T12:00:00') - new Date(b.date + 'T12:00:00'));
        
        let runningBalance = initialBalance || 0; 

        sorted.forEach(p => {
            const day = new Date(p.date + 'T12:00:00').getDate();
            if (!map[day]) map[day] = { items: [], day };
            
            const val = Number(p.value);
            let impact = 0;
            if (p.movementType) {
                impact = p.movementType === 'CREDIT' ? val : -val;
            } else {
                if (p.group === 'RECEITA_BRUTA') impact = val;
                else if (p.group === 'CONTA_CLIENTE') {
                     const isPositive = p.category.includes('Entrada') || p.category.includes('Levantamento');
                     impact = isPositive ? val : -val;
                } else {
                    impact = -val;
                }
            }
            
            runningBalance += impact;
            map[day].items.push({ ...p, impact, runningBalanceAfter: runningBalance });
        });
        
        return Object.values(map).sort((a,b) => a.day - b.day);
    }, [postings, initialBalance]);

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6 mb-6">
            <div className="p-4 border-b border-gray-100 bg-indigo-50/50"><h3 className="font-bold text-indigo-900 text-sm flex items-center gap-2"><i className="fas fa-chart-line"></i> Fluxo de Caixa Diário (Cash Run)</h3></div>
            <div className="p-4 overflow-x-auto flex gap-4 custom-scrollbar pb-6">
                {dailyData.length === 0 ? <p className="text-gray-400 text-sm italic w-full text-center py-4">Sem movimentações previstas neste período.</p> : dailyData.map((dayGroup) => (
                    <div key={dayGroup.day} className="min-w-[180px] bg-gray-50 rounded-lg border border-gray-200 flex flex-col relative group">
                        <div className="bg-white p-2 rounded-t-lg border-b border-gray-100 text-center"><span className="text-xs font-bold text-gray-500 uppercase">DIA</span><div className="text-2xl font-extrabold text-gray-800">{dayGroup.day}</div></div>
                        <div className="p-2 space-y-2 flex-1">{dayGroup.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center text-xs"><span className="truncate max-w-[100px] text-gray-600" title={item.description}>{item.description}</span><span className={`font-bold ${item.impact > 0 ? 'text-green-600' : 'text-red-500'}`}>{item.impact > 0 ? '+' : ''}{Math.round(Math.abs(item.impact))}</span></div>
                        ))}</div>
                        <div className={`p-2 border-t border-gray-200 text-center rounded-b-lg ${dayGroup.items[dayGroup.items.length-1].runningBalanceAfter < 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}><p className="text-[9px] uppercase font-bold opacity-70">Saldo Final</p><p className="font-bold text-sm">{formatCurrency(dayGroup.items[dayGroup.items.length-1].runningBalanceAfter)}</p></div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- 5. COMPONENTE PRINCIPAL (FINANCIAL MANAGER) ---
export const FinancialManager = ({
    extCurrentDate,
    onCurrentDateChange,
    extFilterMode,
    onFilterModeChange,
    openBalanceTrigger = 0,
    openMetasTrigger = 0,
    openNewEntryTrigger = 0,
} = {}) => {
    const [postings, setPostings] = useState([]); 
    const [templates, setTemplates] = useState([]);
    const [editingTx, setEditingTx] = useState(null);
    
    const [view, setView] = useState('POSTINGS'); 
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showBalanceModal, setShowBalanceModal] = useState(false);
    
    // NOVO: Busca e Filtro
    const [searchTerm, setSearchTerm] = useState('');
    const [filterMode, setFilterMode] = useState('DATE'); 

    const [yearPostings, setYearPostings] = useState([]);
    const [yearViewMode, setYearViewMode] = useState('ALL');

    const [drillDownData, setDrillDownData] = useState(null);
    const [categories, setCategories] = useState(DEFAULT_OFFICE_CATEGORIES);
    const [currentDate, setCurrentDate] = useState(new Date());
    const currentYear = currentDate.getFullYear();
    
    const [initialBalance, setInitialBalance] = useState(0);
    // REMOVIDO: const [summary, setSummary] = useState(...) - Agora calculado dinamicamente

    const appId = FIREBASE_CONFIG?.projectId || 'portalrda-f312d';
    const collectionPathPostings = useMemo(() => ['artifacts', appId, 'public', 'data', 'financial_ledger'], [appId]);
    const collectionPathTemplates = useMemo(() => ['artifacts', appId, 'public', 'data', 'office_recurrence_templates'], [appId]);
    const settingsDocPath = useMemo(() => ['artifacts', appId, 'public', 'data', 'office_finance_settings', 'config'], [appId]);

    useEffect(() => {
        loadSettings();
        if (view === 'YEARLY') loadYearlyData();
        else loadTransactions();
    }, [currentDate, collectionPathPostings, collectionPathTemplates, filterMode, view, yearViewMode]);

    useEffect(() => { if (extCurrentDate instanceof Date) setCurrentDate(extCurrentDate); }, [extCurrentDate]);
    useEffect(() => { if (extFilterMode && extFilterMode !== filterMode) setFilterMode(extFilterMode); }, [extFilterMode]);
    useEffect(() => { if (openBalanceTrigger > 0) setShowBalanceModal(true); }, [openBalanceTrigger]);
    useEffect(() => { if (openMetasTrigger > 0) setShowCategoryModal(true); }, [openMetasTrigger]);
    useEffect(() => { if (openNewEntryTrigger > 0) { setEditingTx(null); setShowModal(true); } }, [openNewEntryTrigger]);

    // Filtragem em Memória (Busca Instantânea)
    const filteredPostings = useMemo(() => {
        if (!searchTerm) return postings;
        const lowerTerm = searchTerm.toLowerCase();
        return postings.filter(p => 
            p.description.toLowerCase().includes(lowerTerm) ||
            p.category.toLowerCase().includes(lowerTerm) ||
            (p.linkedName && p.linkedName.toLowerCase().includes(lowerTerm)) ||
            (p.account && p.account.toLowerCase().includes(lowerTerm))
        );
    }, [postings, searchTerm]);

    // CÃLCULO DINÃ‚MICO DE RESUMO (KPIs REATIVOS Ã€ BUSCA)
    const summary = useMemo(() => {
        let income = 0, expense = 0, trust = 0;
        
        // Usa filteredPostings para respeitar a busca
        filteredPostings.forEach(p => {
            const val = Number(p.value);
            if (p.movementType) {
                if (p.group === 'RECEITA_BRUTA' && p.movementType === 'CREDIT') income += val;
                else if (p.group === 'CONTA_CLIENTE') trust += (p.movementType === 'CREDIT' ? val : -val);
                else expense += val;
            } else {
                if (p.group === 'RECEITA_BRUTA') income += val;
                else if (p.group === 'CONTA_CLIENTE') trust += (p.category.includes('Entrada') ? val : -val);
                else expense += val;
            }
        });

        // Templates (Projeção) só entram se NÃƒO houver busca ativa (para não distorcer)
        if (!searchTerm) {
            templates.forEach(t => {
                if (!t.active) return;
                const exists = postings.some(p => p.templateId === t.id); 
                if (!exists) {
                    const val = Number(t.amount);
                    if (t.group === 'RECEITA_BRUTA') income += val;
                    else if (t.group === 'CONTA_CLIENTE') trust += (t.category.includes('Entrada') ? val : -val);
                    else expense += val;
                }
            });
        }

        return { income, expense, trust };
    }, [filteredPostings, templates, searchTerm, postings]);

    const loadSettings = async () => {
        try {
            const data = await loadOfficeFinanceSettings();
            if (data) {
                if (data.categories) setCategories(data.categories);
                if (data.initialBalance) setInitialBalance(Number(data.initialBalance));
            }
        } catch (e) { console.log("Config padrão Office."); }
    };

    const handleSaveCategories = async (newCats) => {
        try {
            await saveOfficeCategories(newCats);
            setCategories(newCats);
        } catch (e) { alert("Erro ao salvar categorias: " + e.message); }
    };

    const handleSaveInitialBalance = async (newBalance) => {
        try {
            await saveOfficeInitialBalance(newBalance);
            setInitialBalance(Number(newBalance));
            setShowBalanceModal(false);
        } catch (e) { alert("Erro ao salvar saldo inicial: " + e.message); }
    };

    const loadTransactions = async () => {
        setLoading(true);
        try {
            const tpls = await loadOfficeTemplates();
            setTemplates(tpls);
            const data = await loadOfficePostingsByMonth(currentDate.getFullYear(), currentDate.getMonth(), filterMode);
            setPostings(data);
        } catch (e) { console.error(e); } finally { setLoading(false); }
    };

    const loadYearlyData = async () => {
        setLoading(true);
        try {
            const data = await loadOfficePostingsByYear(currentYear, filterMode, yearViewMode);
            setYearPostings(data);
            const tpls = await loadOfficeTemplates();
            setTemplates(tpls);
        } catch (e) { console.error("Erro carga anual:", e); }
        finally { setLoading(false); }
    };

    const handleSave = async (formData, mode) => {
        const normalizedGroup = String(formData?.group || formData?.type || '').trim();
        const normalizedCategory = String(formData?.category || formData?.subCategory || '').trim();
        const normalizedLinkedToType = String(formData?.linkedToType || '').trim();
        const normalizedLinkedId = String(formData?.linkedId || '').trim();

        if (normalizedGroup === 'CONTA_CLIENTE' && (normalizedLinkedToType !== 'CLIENT' || !normalizedLinkedId)) {
            alert('COMPLIANCE (TRUST): lançamentos de Conta Cliente devem estar vinculados a um Cliente (linkedToType=CLIENT).');
            return;
        }

        const normalizedClientId =
            normalizedLinkedToType === 'CLIENT' && normalizedLinkedId
                ? normalizedLinkedId
                : String(formData?.clientId || '').trim();

        const amountSource = formData?.amount ?? formData?.value;
        const amountCents = Math.round(Number(amountSource) * 100);
        if (!Number.isFinite(amountCents)) {
            alert('Valor inválido.');
            return;
        }
        const cleanAmount = amountCents / 100;
        const eventClientId = String(normalizedClientId || '').trim();

        // 1. Compliance: Trust vs Op
        if (String(formData?.account || '').includes('Trust') && normalizedGroup === 'DESPESAS_OP') {
            alert("VIOLAÃ‡ÃƒO DE COMPLIANCE: A Conta Trust não pode ser usada para pagar Despesas Operacionais.");
            return;
        }

        // 2. Compliance: Trust Fund Solvency Check (Novo Item A - Blueprint V68)
        if (normalizedGroup === 'CONTA_CLIENTE' && (!formData.movementType || formData.movementType === 'DEBIT')) {
            if (!formData.linkedName) {
                if(!confirm("ALERTA DE SEGURANÃ‡A:\nVocê está registrando uma SAÃDA na Conta Trust sem vincular a um Cliente.\nIsso gera 'Gordura não identificada' e risco de misturar fundos.\n\nDeseja prosseguir mesmo assim?")) return;
            } else {
                // Auditoria de Saldo em Tempo Real
                try {
                    const trustDocs = await findOfficePostingsByClient(normalizedClientId, formData.linkedName);
                    let trustBalance = 0;
                    trustDocs.forEach((d) => {
                        if (formData.id && d.id === formData.id) return;
                        if (d.group === 'CONTA_CLIENTE') {
                            const val = Number(d.value);
                            const isCredit = d.movementType === 'CREDIT' || (d.category && d.category.includes('Entrada'));
                            trustBalance += isCredit ? val : -val;
                        }
                    });

                    const attemptValue = cleanAmount;
                    if (trustBalance < attemptValue) {
                        alert(`ðŸš« BLOQUEIO DE Ã‰TICA (OAB/TRUST):\n\nO cliente "${toTitleCasePt(formData.linkedName)}" não possui fundos suficientes.\n\nðŸ’° Saldo Trust Disponível: ${formatCurrency(trustBalance)}\nðŸ’¸ Tentativa de Saque: ${formatCurrency(attemptValue)}\n\nRegra: Ã‰ proibido usar saldo de outros clientes para cobrir este descoberto.`);
                        return;
                    }
                } catch (err) {
                    console.error("Erro na verificação de Trust:", err);
                    alert("Erro ao verificar saldo de segurança. Operação cancelada.");
                    return;
                }
            }
        }

        try {
            if (!formData.movementType) {
                formData.movementType = normalizedGroup === 'RECEITA_BRUTA' ? 'CREDIT' : 'DEBIT';
            }

            const finalData = {
                ...formData,
                module: 'office',
                group: normalizedGroup,
                category: normalizedCategory,
                ...(normalizedClientId ? { clientId: normalizedClientId } : {}),
                amount: cleanAmount,
            };

            if (formData.id && mode !== 'RECURRING') {
                const status = finalData.isPaid ? 'PAID' : 'PENDING';
                await updateOfficePosting(formData.id, { ...finalData, value: cleanAmount, status });
                try {
                    if (eventClientId) {
                        await logClientPortalEvent({
                            clientId: eventClientId,
                            type: 'FINANCIAL_ENTRY_UPDATED',
                            actorName: 'Escritório',
                            category: 'SYSTEM',
                            payload: {
                                ledgerId: String(formData.id || '').trim() || null,
                                group: normalizedGroup,
                                category: normalizedCategory,
                                value: cleanAmount,
                                status,
                                text: `Lançamento financeiro atualizado: ${String(finalData.description || 'Sem descrição').trim() || 'Sem descrição'}`,
                            },
                        });
                    }
                } catch (e) { void e; }
                setShowModal(false);
                if (view === 'YEARLY') loadYearlyData();
                else loadTransactions();
                return;
            }

            if (mode === 'RECURRING') {
                await createOfficeTemplate({ ...finalData, active: true });
            } else {
                await createOfficePosting({ ...finalData, amount: cleanAmount }, mode);
            }

            try {
                if (eventClientId && mode !== 'RECURRING') {
                    await logClientPortalEvent({
                        clientId: eventClientId,
                        type: 'FINANCIAL_ENTRY_POSTED',
                        actorName: 'Escritório',
                        category: 'SYSTEM',
                        payload: {
                            group: normalizedGroup,
                            category: normalizedCategory,
                            value: cleanAmount,
                            installments: mode === 'SINGLE' ? 1 : Math.max(1, parseInt(formData.repeatCount, 10) || 1),
                            text: `Novo lançamento financeiro registrado para o cliente: ${String(finalData.description || 'Sem descrição').trim() || 'Sem descrição'}`,
                        },
                    });
                }
            } catch (e) { void e; }
            setShowModal(false);
            if (view === 'YEARLY') loadYearlyData();
            else loadTransactions();
        } catch (e) { alert("Erro ao salvar: " + e.message); }
    };

const handleDelete = async (id, isTemplate = false) => {
    if(!confirm("Excluir este item?")) return;
    try {
        const existing = !isTemplate ? postings.find(p => p.id === id) || null : null;
        await deleteOfficePosting(id, isTemplate);
        try {
            const clientId = String(existing?.clientId || '').trim();
            if (!isTemplate && clientId) {
                await logClientPortalEvent({
                    clientId,
                    type: 'FINANCIAL_ENTRY_REMOVED',
                    actorName: 'Escritório',
                    category: 'SYSTEM',
                    payload: {
                        ledgerId: String(id || '').trim() || null,
                        group: String(existing?.group || '').trim() || null,
                        category: String(existing?.category || '').trim() || null,
                        value: Number(existing?.value) || 0,
                        text: `Lançamento financeiro removido: ${String(existing?.description || 'Sem descrição').trim() || 'Sem descrição'}`,
                    },
                });
            }
        } catch (e) { void e; }
        if (view === 'YEARLY') loadYearlyData();
        else loadTransactions();
    } catch(e) { console.error(e); }
};

    const handleEdit = (tx) => {
        setEditingTx(tx);
        setShowModal(true);
    };

    const changeMonth = (offset) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + offset);
        setCurrentDate(newDate);
        if (typeof onCurrentDateChange === 'function') onCurrentDateChange(newDate);
    };

    const handleCellClick = (group, monthIndex) => {
        const items = [];
        let total = 0;
        yearPostings.forEach(p => {
            const dateStr = filterMode === 'COMPETENCE' ? p.competenceDate : p.date;
            const pMonth = parseInt(dateStr.split('-')[1]) - 1; 
            if (p.group === group && pMonth === monthIndex) {
                items.push({
                    description: p.description,
                    value: Number(p.value),
                    category: p.category,
                    status: p.status,
                    account: p.account, 
                    linkedName: p.linkedName,
                    movementType: p.movementType,
                    isProjection: false
                });
                total += Number(p.value);
            }
        });
        if (yearViewMode === 'ALL') {
             const currentMonthIdx = new Date().getMonth();
             if (monthIndex >= currentMonthIdx) {
                 templates.forEach(t => {
                     if (t.active && t.group === group) {
                         const exists = yearPostings.some(p => {
                             const pMonth = parseInt((filterMode === 'COMPETENCE' ? p.competenceDate : p.date).split('-')[1]) - 1;
                             return p.templateId === t.id && pMonth === monthIndex;
                         });
                         if (!exists) {
                             items.push({
                                 description: t.description,
                                 value: Number(t.amount),
                                 category: t.category,
                                 status: 'PROJECTION',
                                 account: t.account, 
                                 linkedName: t.linkedName,
                                 isProjection: true
                             });
                             total += Number(t.amount);
                         }
                     }
                 });
             }
        }
        const monthName = new Date(0, monthIndex).toLocaleString('pt-BR', { month: 'long' });
        setDrillDownData({
            title: `${group} - ${monthName}/${currentYear}`,
            monthLabel: monthName.toUpperCase(),
            items: items.sort((a,b) => b.value - a.value),
            total
        });
    };

    const renderYearlyView = () => {
        const months = Array.from({length: 12}, (_, i) => new Date(0, i).toLocaleString('pt-BR', { month: 'short' }));
        const groups = ['RECEITA_BRUTA', 'CUSTOS_DIRETOS', 'DESPESAS_OP', 'CONTA_CLIENTE'];
        const grid = {};
        groups.forEach(g => grid[g] = Array(12).fill(0));
        
        yearPostings.forEach(p => {
            const dateStr = filterMode === 'COMPETENCE' ? p.competenceDate : p.date;
            const month = parseInt(dateStr.split('-')[1]) - 1;
            if (grid[p.group]) grid[p.group][month] += Number(p.value);
        });

        if (yearViewMode === 'ALL') {
            const currentMonthIdx = new Date().getMonth();
            templates.forEach(t => {
                if (t.active && grid[t.group]) {
                    for (let i = 0; i < 12; i++) {
                        if (i >= currentMonthIdx) {
                             const exists = yearPostings.some(p => {
                                 const pMonth = parseInt((filterMode === 'COMPETENCE' ? p.competenceDate : p.date).split('-')[1]) - 1;
                                 return p.templateId === t.id && pMonth === i;
                             });
                             if (!exists) grid[t.group][i] += Number(t.amount);
                        }
                    }
                }
            });
        }

        return (
            <div className="overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="flex justify-end p-2 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-2 bg-white rounded-md border border-gray-200 p-1">
                        <button onClick={() => setYearViewMode('ALL')} className={`px-3 py-1 text-[10px] font-bold ${yearViewMode === 'ALL' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-50'}`}>PREVISTO (Tudo)</button>
                        <button onClick={() => setYearViewMode('PAID_ONLY')} className={`px-3 py-1 text-[10px] font-bold ${yearViewMode === 'PAID_ONLY' ? 'bg-green-100 text-green-700' : 'text-gray-500 hover:bg-gray-50'}`}>REALIZADO (Pago)</button>
                    </div>
                </div>
                <table className="w-full text-sm text-right border-collapse">
                    <thead className="bg-gray-100 text-gray-600 font-bold uppercase text-[10px] tracking-wider">
                        <tr>
                            <th className="p-3 text-left border-r border-gray-200 sticky left-0 bg-gray-100 z-10">Grupo Contábil</th>
                            {months.map(m => <th key={m} className="p-3 border-r border-gray-200 min-w-[80px]">{m.toUpperCase()}</th>)}
                            <th className="p-3 bg-gray-200 min-w-[100px]">TOTAL</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {groups.map(g => {
                            const rowTotal = grid[g].reduce((a, b) => a + b, 0);
                            const label = categories[g]?.label.split('. ')[1] || g;
                            return (
                                <tr key={g} className="hover:bg-indigo-50/50 group">
                                    <td className="p-3 text-left font-bold text-gray-700 border-r border-gray-200 sticky left-0 bg-white group-hover:bg-indigo-50/50 text-xs">{label}</td>
                                    {grid[g].map((val, idx) => (
                                        <td key={idx} onClick={() => handleCellClick(g, idx)} className={`p-3 border-r border-gray-100 cursor-pointer hover:bg-indigo-100 text-xs ${val !== 0 ? (g === 'RECEITA_BRUTA' ? 'text-green-600 font-medium' : 'text-red-600') : 'text-gray-200'}`} title="Clique para ver detalhes">
                                            {val !== 0 ? formatCompact(val) : '-'}
                                        </td>
                                    ))}
                                    <td className="p-3 font-bold bg-gray-50 border-l border-gray-200 text-xs">{formatCompact(rowTotal)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const monthLabel = currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

    // Pendências urgentes (Escritório): Firestore postings PENDING + JSON canônico (escopo office)
    const jsonPendenciasOffice = useJsonPendencias({ escopo: 'office' });
    const pendencyItemsOffice = useMemo(() => {
        const fromFirestore = postingsToPendencyItems(postings, { module: 'office' });
        return [...fromFirestore, ...jsonPendenciasOffice];
    }, [postings, jsonPendenciasOffice]);

    const handleMarkPaidOffice = async (item) => {
        if (!item || !item.id || item.fonte !== 'firestore') return;
        try {
            await markOfficePostingPaid(item.id, new Date().toISOString().split('T')[0]);
            loadTransactions();
        } catch (e) {
            alert('Erro ao marcar como pago: ' + e.message);
        }
    };

    return (
        <div className="space-y-4 pb-10">
            <FinanceSectionHeader moduleKey="office" monthDate={currentDate} />
            <PendenciasUrgentesPanel
                items={pendencyItemsOffice}
                onMarkPaid={handleMarkPaidOffice}
            />

            {/* CARDS KPI (AGORA DINÃ‚MICOS COM SEARCH) */}
            {view !== 'YEARLY' && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {Object.entries(categories).map(([key, cat]) => {
                        let val = 0;
                        if (key === 'RECEITA_BRUTA') val = summary.income;
                        if (key === 'DESPESAS_OP') val = summary.expense;
                        if (key === 'CONTA_CLIENTE') val = summary.trust;
                        if (key === 'CUSTOS_DIRETOS') val = 0;
                        const budget = cat.budget || 0;
                        const percent = budget > 0 ? (val / budget) * 100 : 0;
                        const isOver = percent > 100;
                        return (
                            <div key={key} className={`bg-white p-4 rounded-xl border shadow-md flex flex-col justify-between ${isOver && key !== 'RECEITA_BRUTA' ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}>
                                <div className="flex justify-between items-start mb-2">
                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{cat.label.split('. ')[1]}</span>
                                    <span className={`text-lg font-extrabold ${key === 'RECEITA_BRUTA' ? 'text-green-600' : (key === 'CONTA_CLIENTE' ? 'text-blue-800' : 'text-red-700')}`}>{formatCurrency(val)}</span>
                                </div>
                                {key !== 'RECEITA_BRUTA' && budget > 0 && (
                                    <div className="w-full">
                                        <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                                            <span>Meta: {formatCurrency(budget)}</span>
                                            <span className={isOver ? 'text-red-600 font-bold' : ''}>{Math.round(percent)}%</span>
                                        </div>
                                        <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                                            <div className={`h-full ${isOver ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(percent, 100)}%` }}></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* FLUXO DE CAIXA DIÃRIO (APENAS MENSAL) */}
            {view === 'POSTINGS' && <DailyLiquidityPanel postings={filteredPostings} initialBalance={initialBalance} />}

            {/* NAVEGAÃ‡ÃƒO DE ABAS & CONTEÃšDO PRINCIPAL (PARIDADE TOTAL M12) */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-300 overflow-hidden">
                {/* Header das Abas */}
                <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-700 text-sm">Visualização de Dados</h3>
                    <div className="flex gap-2">
                         <button onClick={() => setView('POSTINGS')} className={`text-xs font-bold px-3 py-1 rounded flex items-center gap-1 ${view === 'POSTINGS' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-400 hover:bg-gray-100'}`}>
                            <i className="fas fa-list"></i> Extrato Mensal
                         </button>
                         <button onClick={() => setView('TEMPLATES')} className={`text-xs font-bold px-3 py-1 rounded flex items-center gap-1 ${view === 'TEMPLATES' ? 'bg-orange-100 text-orange-700' : 'text-gray-400 hover:bg-gray-100'}`}>
                            <i className="fas fa-sync"></i> Assinaturas/Fixos
                         </button>
                         <button onClick={() => { setView('YEARLY'); loadYearlyData(); }} className={`text-xs font-bold px-3 py-1 rounded flex items-center gap-1 ${view === 'YEARLY' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                            <i className="fas fa-calendar-alt"></i> Visão Anual (Macro)
                         </button>
                         
                         <button onClick={() => exportToCSV(filteredPostings)} className="text-xs font-bold px-3 py-1 rounded text-green-700 hover:bg-green-50 border border-green-200 flex items-center gap-1">
                            <i className="fas fa-file-excel"></i> CSV {searchTerm ? '(Filtrado)' : ''}
                         </button>
                    </div>
                </div>

                {/* BARRA DE BUSCA (V11.5 - UX UPGRADE) */}
                {view === 'POSTINGS' && (
                    <div className="bg-gray-50 p-2 px-4 border-b border-gray-200 flex justify-end items-center gap-2">
                        {searchTerm && <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">FILTRO ATIVO: KPIs Atualizados</span>}
                        <div className="relative w-64">
                            <input 
                                type="text" 
                                placeholder="Buscar (Nome, Valor, Categoria)..." 
                                className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 outline-none"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                            <i className="fas fa-search absolute left-2.5 top-2 text-gray-400 text-xs"></i>
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1.5 text-gray-400 hover:text-red-500">
                                    <i className="fas fa-times text-xs"></i>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* 1. VISÃƒO ANUAL */}
                {view === 'YEARLY' && renderYearlyView()}

                {/* 2. EXTRATO MENSAL (PADRÃƒO UNIFICADO PF/PJ V1.0 - 01/05/2026) */}
                {view === 'POSTINGS' && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-[11px]">
                            <thead className="bg-gray-100 border-b border-gray-300 sticky top-0 z-10">
                                <tr>
                                    <th className="px-2.5 py-1 text-[10px] font-bold text-gray-600 uppercase tracking-wide w-[80px]">Vencimento</th>
                                    <th className="px-2.5 py-1 text-[10px] font-bold text-gray-600 uppercase tracking-wide w-[80px]">Pagamento</th>
                                    <th className="px-2.5 py-1 text-[10px] font-bold text-gray-600 uppercase tracking-wide w-[110px]">Recorrência</th>
                                    <th className="px-2.5 py-1 text-[10px] font-bold text-gray-600 uppercase tracking-wide">Descrição</th>
                                    <th className="px-2.5 py-1 text-[10px] font-bold text-gray-600 uppercase tracking-wide w-[140px]">Vínculo</th>
                                    <th className="px-2.5 py-1 text-[10px] font-bold text-gray-600 uppercase tracking-wide w-[110px]">Categoria</th>
                                    <th className="px-2.5 py-1 text-[10px] font-bold text-gray-600 uppercase tracking-wide w-[70px]">Status</th>
                                    <th className="px-2.5 py-1 text-[10px] font-bold text-gray-600 uppercase tracking-wide text-right w-[100px]">Valor</th>
                                    <th className="px-2 py-1 w-[50px]"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPostings.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-gray-400 text-[12px]">Nenhum lançamento encontrado.</td></tr>}
                                {filteredPostings.map((t, idx) => {
                                    const isPaid = t.status === 'PAID';
                                    const zebra = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50';
                                    const isCredit = t.movementType === 'CREDIT' || (!t.movementType && t.group === 'RECEITA_BRUTA');
                                    const parcelaMatch = (t.description||'').match(/\((\d+\/\d+)\)/);
                                    const parcela = t.parcela || (parcelaMatch ? parcelaMatch[1] : null);
                                    const recorr = parcela ? 'Parcelado' : (t.recorrencia || ((t.description||'').toLowerCase().includes('mensal') ? 'Mensal' : 'Ãšnica'));
                                    const rc = { 'Parcelado':'bg-amber-50 text-amber-700 border-amber-200', 'Mensal':'bg-blue-50 text-blue-700 border-blue-200', 'Anual':'bg-purple-50 text-purple-700 border-purple-200', 'Semestral':'bg-violet-50 text-violet-700 border-violet-200', 'Trimestral':'bg-indigo-50 text-indigo-700 border-indigo-200', 'Bimestral':'bg-cyan-50 text-cyan-700 border-cyan-200', 'Ãšnica':'bg-gray-50 text-gray-600 border-gray-200' };
                                    const catColor = (categories[t.group]?.color) || 'bg-gray-100 text-gray-700 border-gray-200';
                                    return (
                                        <tr key={t.id} className={`${zebra} hover:bg-blue-50/40 border-b border-gray-100 group transition-colors`}>
                                            <td className="px-2.5 py-1 font-mono text-[10px] text-gray-700 whitespace-nowrap">{new Date(t.date + 'T12:00:00').toLocaleDateString('pt-BR')}</td>
                                            <td className="px-2.5 py-1 font-mono text-[10px] whitespace-nowrap">{t.settlementDate ? <span className="text-gray-700">{new Date(t.settlementDate + 'T12:00:00').toLocaleDateString('pt-BR')}</span> : <span className="text-gray-300">â€”</span>}</td>
                                            <td className="px-2.5 py-1 whitespace-nowrap"><span className={`inline-block px-1.5 py-0.5 rounded border font-bold text-[9px] ${rc[recorr]||rc['Ãšnica']}`}>{recorr}{parcela && <span className="ml-1 font-mono font-normal opacity-80">{parcela}</span>}</span></td>
                                            <td className="px-2.5 py-1 text-gray-800 leading-tight text-[11px]" title={`${t.description}${t.account ? ' Â· ' + t.account : ''}`}>{t.description}{t.account && <span className="ml-1 text-[9px] text-gray-400 font-mono">({t.account})</span>}</td>
                                            <td className="px-2.5 py-1">{t.linkedName ? <span className="inline-block bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200 text-[9px] font-medium whitespace-nowrap" title={toTitleCasePt(t.linkedName)}>{toTitleCasePt(t.linkedName).slice(0,18)}{toTitleCasePt(t.linkedName).length > 18 ? 'â€¦' : ''}</span> : <span className="text-gray-300">â€”</span>}</td>
                                            <td className="px-2.5 py-1"><span className={`inline-block px-1.5 py-0.5 rounded border font-medium text-[9px] whitespace-nowrap ${catColor}`}>{t.category}</span></td>
                                            <td className="px-2.5 py-1">
                                                <span className="inline-flex items-center gap-1">
                                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${isPaid ? 'bg-green-500' : 'bg-orange-400'}`}></span>
                                                    <span className={`font-medium text-[10px] ${isPaid ? 'text-green-700' : 'text-orange-600'}`}>{isPaid ? 'Pago' : 'Aberto'}</span>
                                                </span>
                                            </td>
                                            <td className={`px-2.5 py-1 text-right font-mono font-bold tabular-nums text-[11px] whitespace-nowrap ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                                                {isCredit ? '+' : 'âˆ’'} {formatCurrency(t.value)}
                                            </td>
                                            <td className="px-1.5 py-1 text-right whitespace-nowrap">
                                                <button onClick={() => handleEdit(t)} className="text-gray-400 hover:text-blue-600 opacity-50 group-hover:opacity-100 px-1" title="Editar"><i className="fas fa-pen text-[9px]"></i></button>
                                                <button onClick={() => handleDelete(t.id)} className="text-gray-400 hover:text-red-500 opacity-50 group-hover:opacity-100 px-1" title="Excluir"><i className="fas fa-trash text-[9px]"></i></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            {filteredPostings.length > 0 && (() => {
                                const totalNet = filteredPostings.reduce((sum, t) => {
                                    const isCredit = t.movementType === 'CREDIT' || (!t.movementType && t.group === 'RECEITA_BRUTA');
                                    return sum + (isCredit ? Number(t.value) : -Number(t.value));
                                }, 0);
                                const isNeg = totalNet < 0;
                                return (
                                    <tfoot className={`border-t-2 font-bold ${isNeg ? 'bg-red-50 border-red-300' : 'bg-green-50/70 border-green-300'}`}>
                                        <tr>
                                            <td colSpan="7" className={`px-2.5 py-1.5 text-[10px] uppercase tracking-wide text-right ${isNeg ? 'text-red-700' : 'text-green-700'}`}>
                                                Saldo do Período ({filteredPostings.length} {filteredPostings.length === 1 ? 'lançamento' : 'lançamentos'})
                                            </td>
                                            <td className={`px-2.5 py-1.5 text-right font-mono tabular-nums text-[13px] ${isNeg ? 'text-red-700' : 'text-green-700'}`}>
                                                {formatCurrency(totalNet)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                );
                            })()}
                        </table>
                    </div>
                )}

                {/* 3. ASSINATURAS (TEMPLATES) */}
                {view === 'TEMPLATES' && (
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-gray-50/50 min-h-[300px]">
                        {templates.map(t => (
                            <div key={t.id} className="bg-white p-4 rounded-lg border border-orange-100 shadow-sm relative group hover:shadow-md">
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100"><button onClick={() => handleDelete(t.id, true)} className="text-red-400 hover:text-red-600"><i className="fas fa-trash"></i></button></div>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="bg-orange-100 text-orange-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">Recorrente</span>
                                    <span className="text-[9px] text-gray-400 font-bold uppercase">Dia {t.anchorDay}</span>
                                </div>
                                <h4 className="font-bold text-gray-800">{t.description}</h4>
                                {t.linkedName && <p className="text-[10px] text-blue-600 font-bold mt-1 bg-blue-50 inline-block px-1 rounded">Ref: {toTitleCasePt(t.linkedName)}</p>}
                                <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
                                    <span className="text-xs text-gray-500">{t.category}</span>
                                    <p className="text-lg font-bold text-gray-700">{formatCurrency(t.amount)}</p>
                                </div>
                            </div>
                        ))}
                        {templates.length === 0 && (
                            <div className="col-span-3 text-center p-12 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                                <i className="fas fa-sync-alt text-4xl mb-2 opacity-20"></i>
                                <p>Nenhuma despesa ou receita recorrente cadastrada.</p>
                                <p className="text-xs">Use o botão "Lançamento" e selecione "Recorrente" para criar.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* MODAIS */}
            {showModal && <OfficeTransactionModal onClose={() => setShowModal(false)} onSave={handleSave} categories={categories} history={postings} initialData={editingTx} />}
            {showCategoryModal && <CategoryManagerModal onClose={() => setShowCategoryModal(false)} categories={categories} onSaveCategories={handleSaveCategories} />}
            {showBalanceModal && <InitialBalanceModal onClose={() => setShowBalanceModal(false)} onSave={handleSaveInitialBalance} currentValue={initialBalance} />}
            {drillDownData && <DrillDownModal onClose={() => setDrillDownData(null)} data={drillDownData} title={drillDownData.title} monthLabel={drillDownData.monthLabel} />}
        </div>
    );
};
